/**
 * Two things a crew does between game nights.
 *
 * An announcement, for the thing everybody has to read once rather than four
 * times, and a challenge - something to do together with no prize, no ranking
 * and no way to lose, because a crew is a group of friends and not a
 * workplace with targets.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { PublicUser } from '@/lib/types';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { GameMark } from './GameMark';
import { Button, Field, Modal, useToast } from './ui';
import { Select } from './Select';
import type { Crew } from './Crew';

/* ------------------------------------------------------------ announcing */

export function Announce({ crew, onClose }: { crew: Crew; onClose: () => void }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();

  async function send() {
    if (!title.trim() && !body.trim()) { toast('Say something first.', 'bad'); return; }
    setBusy(true);
    try {
      await api.post('/api/crews/' + crew.id + '/announce', { title: title.trim(), body: body.trim() });
      void queryClient.invalidateQueries({ queryKey: ['crew'] });
      toast('Posted and pinned. Everybody was told once.', 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Announce something"
      subtitle={'Everybody in ' + crew.name + ' is told once, and it stays pinned at the top of the chat.'}
      onClose={onClose}
      footer={
        <>
          <Button kind="outline" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={busy} onClick={() => void send()}>{busy ? 'Posting…' : 'Post it'}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Headline">
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 80))}
            placeholder="No game night this Friday"
            className="h-[var(--h-md)] w-full rounded-[calc(12px*var(--radius-mul,1))] border border-line bg-surface-1 px-3 text-[14px] outline-none transition-colors placeholder:text-ghost focus:border-accent"
          />
        </Field>
        <Field label="The rest of it" hint={body.length + '/800'}>
          <textarea
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 800))}
            placeholder="Back to normal next week. The Minecraft server is staying up in the meantime."
            className="w-full resize-none rounded-[var(--radius-s)] border border-line bg-surface-1 p-3 text-[13.5px] outline-none transition-colors placeholder:text-ghost focus:border-accent"
          />
        </Field>
        <p className="m-0 text-[12.5px] text-mute">
          One notification each, whatever their settings say about crew chat. Use it for the things that are worth that.
        </p>
      </div>
    </Modal>
  );
}

/* ----------------------------------------------------------- challenges */

export type Challenge = {
  id: string;
  title: string;
  note: string;
  gameId: string | null;
  goal: number;
  unit: string;
  endsAt: number;
  over: boolean;
  createdBy: string;
  joinedCount: number;
  joined: PublicUser[];
  doneCount: number;
  mine: number;
  imIn: boolean;
};

export function Challenges({ crew, canManage }: { crew: Crew; canManage: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [making, setMaking] = useState(false);

  const q = useQuery({
    queryKey: ['crew-challenges', crew.id],
    queryFn: () => api.get<{ challenges: Challenge[] }>('/api/crews/' + crew.id + '/challenges'),
    enabled: !!crew.myRole,
    staleTime: 30_000
  });

  const act = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.post<{ challenge: Challenge }>('/api/crews/' + crew.id + '/challenges/' + id, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['crew-challenges', crew.id] }),
    onError: (err: Error) => toast(err.message, 'bad')
  });

  const drop = useMutation({
    mutationFn: (id: string) => api.del('/api/crews/' + crew.id + '/challenges/' + id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crew-challenges', crew.id] });
      toast('Challenge ended', 'good');
    },
    onError: (err: Error) => toast(err.message, 'bad')
  });

  if (!crew.myRole) return null;
  const list = q.data?.challenges ?? [];
  if (!list.length && !canManage) return null;

  return (
    <section className="pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <div className="mb-3 flex items-center gap-2.5">
        <Icon name="target" size={18} className="text-ink" />
        <h3 className="m-0 flex-1 text-[16px] font-bold text-ink">Challenges</h3>
        {canManage ? <Button size="sm" kind="outline" onClick={() => setMaking(true)}>Set one</Button> : null}
      </div>

      {!list.length ? (
        <p className="m-0 text-[13.5px] text-mute">
          Something to do together - try a new game, play a beginner-friendly night. No prize, nobody loses, taking part is a button.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {list.map((c) => {
            const share = Math.min(100, Math.round((c.mine / Math.max(1, c.goal)) * 100));
            const days = Math.max(0, Math.ceil((c.endsAt - Date.now()) / (24 * 60 * 60 * 1000)));
            return (
              <li key={c.id} className="rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3">
                <div className="flex items-center gap-2.5">
                  {c.gameId ? <GameMark id={c.gameId} size={24} plain /> : <Icon name="target" size={18} className="text-mute" />}
                  <div className="min-w-0 flex-1">
                    <p className="m-0 truncate text-[14px] font-bold text-ink">{c.title}</p>
                    <p className="m-0 truncate text-[12px] text-mute">
                      {c.over ? 'Finished' : days + (days === 1 ? ' day left' : ' days left')}
                      {' · '}{c.joinedCount} taking part
                      {c.doneCount ? ' · ' + c.doneCount + ' done' : ''}
                    </p>
                  </div>
                  {canManage ? (
                    <button
                      type="button"
                      aria-label={'End ' + c.title}
                      onClick={() => drop.mutate(c.id)}
                      className="grid h-7 w-7 flex-none place-items-center rounded-full text-ghost transition-colors hover:bg-surface-3 hover:text-ink"
                    >
                      <Icon name="close" size={13} />
                    </button>
                  ) : null}
                </div>

                {c.note ? <p className="m-0 mt-1.5 text-[12.5px] text-dim">{c.note}</p> : null}

                {c.imIn ? (
                  <div className="mt-2.5">
                    <div className="flex items-center gap-2">
                      <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-3">
                        <span
                          className="block h-full rounded-full bg-accent transition-[width] duration-[400ms]"
                          style={{ width: share + '%' }}
                        />
                      </span>
                      <span className="flex-none text-[12px] font-bold text-dim tabular-nums">{c.mine}/{c.goal} {c.unit}</span>
                    </div>
                    {!c.over ? (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <Button size="sm" kind="outline" onClick={() => act.mutate({ id: c.id, body: { progress: c.mine + 1 } })}>
                          +1
                        </Button>
                        {c.mine > 0 ? (
                          <Button size="sm" kind="plain" onClick={() => act.mutate({ id: c.id, body: { progress: Math.max(0, c.mine - 1) } })}>
                            −1
                          </Button>
                        ) : null}
                        <Button size="sm" kind="plain" onClick={() => act.mutate({ id: c.id, body: { join: false } })}>Drop out</Button>
                      </div>
                    ) : null}
                  </div>
                ) : !c.over ? (
                  <div className="mt-2.5 flex items-center gap-2">
                    <span className="flex items-center">
                      {c.joined.slice(0, 4).map((u, i) => (
                        <span key={u.id} className="rounded-full ring-2 ring-[var(--bg-2)]" style={{ marginLeft: i ? -8 : 0 }}>
                          <Avatar user={u} size={22} />
                        </span>
                      ))}
                    </span>
                    <span className="flex-1" />
                    <Button size="sm" kind="primary" onClick={() => act.mutate({ id: c.id, body: { join: true } })}>Take part</Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {making ? <NewChallenge crew={crew} onClose={() => setMaking(false)} /> : null}
    </section>
  );
}

function NewChallenge({ crew, onClose }: { crew: Crew; onClose: () => void }) {
  const catalog = useSession((s) => s.catalog);
  const queryClient = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [gameId, setGameId] = useState('');
  const [goal, setGoal] = useState(3);
  const [unit, setUnit] = useState('games');
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!title.trim()) { toast('Give it a name.', 'bad'); return; }
    setBusy(true);
    try {
      await api.post('/api/crews/' + crew.id + '/challenges', { title: title.trim(), note, gameId: gameId || null, goal, unit, days });
      void queryClient.invalidateQueries({ queryKey: ['crew-challenges', crew.id] });
      toast('Set. The crew was told, and nobody is entered without pressing it.', 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const field = 'h-[var(--h-md)] w-full rounded-[calc(12px*var(--radius-mul,1))] border border-line bg-surface-1 px-3 text-[14px] outline-none transition-colors placeholder:text-ghost focus:border-accent';

  return (
    <Modal
      title="Set a challenge"
      subtitle="Something to do together. No prize, no ranking, and nobody is entered without pressing Take part."
      onClose={onClose}
      footer={
        <>
          <Button kind="outline" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={busy} onClick={() => void save()}>{busy ? 'Setting…' : 'Set it'}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="What is it">
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value.slice(0, 60))} placeholder="Play something none of us have tried" className={field} />
        </Field>
        <Field label="Any detail" hint={note.length + '/200'}>
          <input value={note} onChange={(e) => setNote(e.target.value.slice(0, 200))} placeholder="Anything on the shelf that nobody has opened." className={field} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Game">
            <Select
              value={gameId}
              onChange={setGameId}
              options={[{ value: '', label: 'Any game' }, ...(catalog?.games ?? []).map((g) => ({ value: g.id, label: g.name }))]}
            />
          </Field>
          <Field label="Runs for">
            <Select
              value={String(days)}
              onChange={(v) => setDays(Number(v))}
              options={([['3', '3 days'], ['7', 'A week'], ['14', 'A fortnight'], ['30', 'A month']] as const).map(([value, label]) => ({ value, label }))}
            />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="How many">
            <input
              type="number"
              min={1}
              max={100}
              value={goal}
              onChange={(e) => setGoal(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
              className={field}
            />
          </Field>
          <Field label="Of what">
            <input value={unit} onChange={(e) => setUnit(e.target.value.slice(0, 20))} placeholder="games" className={field} />
          </Field>
        </div>
        <p className="m-0 text-[12.5px] text-mute">
          People count their own. Nothing here is worth cheating at, and a crew that wants to check can look at the number.
        </p>
      </div>
    </Modal>
  );
}
